import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RichTextEditor from '../Common/RichTextEditor';
import blogService from '../../services/blogService';
import { generateSlug } from '../../utils/blogHelpers';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    tags: [],
    metaDescription: '',
    isPublished: false
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    if (isEditing) {
      loadBlog();
    }
    
    // Cleanup blob URLs on unmount
    return () => {
      if (formData.featuredImage && formData.featuredImage.startsWith('blob:')) {
        URL.revokeObjectURL(formData.featuredImage);
      }
    };
  }, [id, isEditing]);

  // Auto-save functionality
  useEffect(() => {
    if (!autoSaveEnabled || !formData.title.trim()) return;

    const autoSaveTimer = setTimeout(() => {
      if (isEditing) {
        handleAutoSave();
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formData, autoSaveEnabled, isEditing]);

  const loadBlog = async () => {
    try {
      setLoading(true);
      const result = await blogService.getBlog(id);
      
      if (result.success) {
        setFormData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load blog post');
      console.error('Error loading blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async () => {
    if (!formData.title.trim() || saving) return;

    try {
      const result = await blogService.updateBlog(id, {
        ...formData,
        updatedAt: Date.now()
      });

      if (result.success) {
        setLastSaved(new Date());
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-generate excerpt from content if not manually set
    if (field === 'content' && !formData.excerpt) {
      const textContent = value.replace(/<[^>]*>/g, '');
      const autoExcerpt = textContent.substring(0, 160) + (textContent.length > 160 ? '...' : '');
      setFormData(prev => ({
        ...prev,
        excerpt: autoExcerpt
      }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (file) => {
    try {
      setImageUploading(true);
      // This would typically upload to a service like Firebase Storage
      // For now, we'll create a placeholder URL with proper cleanup
      const imageUrl = URL.createObjectURL(file);
      
      // Store the blob URL for cleanup later
      const currentBlobUrl = formData.featuredImage;
      if (currentBlobUrl && currentBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentBlobUrl);
      }
      
      setFormData(prev => ({
        ...prev,
        featuredImage: imageUrl
      }));
      
      return imageUrl;
    } catch (err) {
      setError('Failed to upload image');
      console.error('Error uploading image:', err);
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    
    if (!formData.content.trim()) {
      setError('Content is required');
      return false;
    }

    if (formData.title.length > 100) {
      setError('Title must be less than 100 characters');
      return false;
    }

    if (formData.metaDescription.length > 160) {
      setError('Meta description must be less than 160 characters');
      return false;
    }

    return true;
  };

  const handleSave = async (publish = false) => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      const blogData = {
        ...formData,
        isPublished: publish,
        slug: generateSlug(formData.title),
        updatedAt: Date.now(),
        ...(publish && !formData.publishedAt && { publishedAt: Date.now() }),
        ...(isEditing ? {} : { 
          createdAt: Date.now(),
          author: 'Admin' // This should come from auth context
        })
      };

      const result = isEditing 
        ? await blogService.updateBlog(id, blogData)
        : await blogService.createBlog(blogData);

      if (result.success) {
        setSuccess(publish ? 'Blog published successfully!' : 'Blog saved successfully!');
        setLastSaved(new Date());
        
        if (!isEditing) {
          // Redirect to edit mode after creating
          navigate(`/admin/blog/edit/${result.data.id}`);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to save blog post');
      console.error('Error saving blog:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/admin/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading blog editor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ paddingTop: '120px' }}>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-0" style={{ paddingTop: '115px' }}>
                {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h1>
              {lastSaved && (
                <small className="text-muted">
                  Last saved: {lastSaved.toLocaleTimeString()}
                </small>
              )}
            </div>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                className="btn btn-outline-primary"
                onClick={() => handleSave(false)}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                    Saving...
                  </>
                ) : (
                  'Save Draft'
                )}
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => handleSave(true)}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                    Publishing...
                  </>
                ) : (
                  formData.isPublished ? 'Update & Publish' : 'Publish'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="bi bi-check-circle-fill me-2"></i>
              {success}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSuccess(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        {/* Main Editor */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              {/* Title */}
              <div className="mb-3">
                <label htmlFor="title" className="form-label fw-medium">
                  Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-lg"
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter blog title..."
                  maxLength="100"
                />
                <div className="form-text">
                  {formData.title.length}/100 characters
                </div>
              </div>

              {/* Content Editor */}
              <div className="mb-3">
                <label className="form-label fw-medium">
                  Content <span className="text-danger">*</span>
                </label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => handleInputChange('content', content)}
                  placeholder="Write your blog content here..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          {/* Auto-save Settings */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0">
              <h6 className="mb-0">Settings</h6>
            </div>
            <div className="card-body">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="autoSave"
                  checked={autoSaveEnabled}
                  onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="autoSave">
                  Enable auto-save
                </label>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0">
              <h6 className="mb-0">Featured Image</h6>
            </div>
            <div className="card-body">
              {formData.featuredImage ? (
                <div className="mb-3">
                  <img
                    src={formData.featuredImage}
                    alt="Featured"
                    className="img-fluid rounded"
                    style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                  />
                  <button
                    className="btn btn-sm btn-outline-danger mt-2"
                    onClick={() => handleInputChange('featuredImage', '')}
                  >
                    Remove Image
                  </button>
                </div>
              ) : (
                <div className="text-center py-3">
                  <i className="bi bi-image display-4 text-muted mb-2"></i>
                  <p className="text-muted small">No featured image selected</p>
                </div>
              )}
              
              {/* File Upload */}
              <div className="mb-3">
                <label className="form-label small">Upload from device:</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleImageUpload(e.target.files[0]);
                    }
                  }}
                  disabled={imageUploading}
                />
              </div>

              {/* URL Input */}
              <div className="mb-3">
                <label className="form-label small">Or enter image URL:</label>
                <div className="input-group">
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://example.com/image.jpg"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const url = e.target.value.trim();
                        if (url) {
                          handleInputChange('featuredImage', url);
                          e.target.value = '';
                        }
                      }
                    }}
                    disabled={imageUploading}
                  />
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    onClick={(e) => {
                      const input = e.target.previousElementSibling;
                      const url = input.value.trim();
                      if (url) {
                        handleInputChange('featuredImage', url);
                        input.value = '';
                      }
                    }}
                    disabled={imageUploading}
                  >
                    Add
                  </button>
                </div>
                <div className="form-text">
                  Enter a direct link to an image (jpg, png, gif, webp)
                </div>
              </div>
              
              {imageUploading && (
                <div className="text-center mt-2">
                  <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <small className="text-muted ms-2">Uploading...</small>
                </div>
              )}
            </div>
          </div>

          {/* Excerpt */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0">
              <h6 className="mb-0">Excerpt</h6>
            </div>
            <div className="card-body">
              <textarea
                className="form-control"
                rows="3"
                value={formData.excerpt}
                onChange={(e) => handleInputChange('excerpt', e.target.value)}
                placeholder="Brief description of the blog post..."
                maxLength="300"
              />
              <div className="form-text">
                {formData.excerpt.length}/300 characters
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0">
              <h6 className="mb-0">Tags</h6>
            </div>
            <div className="card-body">
              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  onClick={handleAddTag}
                >
                  Add
                </button>
              </div>
              
              <div className="d-flex flex-wrap gap-1">
                {formData.tags && formData.tags.map((tag, index) => (
                  <span key={index} className="badge bg-primary">
                    {tag}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-1"
                      style={{ fontSize: '0.7em' }}
                      onClick={() => handleRemoveTag(tag)}
                    ></button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* SEO */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0">
              <h6 className="mb-0">SEO</h6>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label htmlFor="metaDescription" className="form-label">
                  Meta Description
                </label>
                <textarea
                  className="form-control"
                  id="metaDescription"
                  rows="2"
                  value={formData.metaDescription}
                  onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                  placeholder="SEO meta description..."
                  maxLength="160"
                />
                <div className="form-text">
                  {formData.metaDescription.length}/160 characters
                </div>
              </div>
              
              {formData.title && (
                <div className="mb-2">
                  <small className="text-muted">URL Slug:</small>
                  <div className="font-monospace small text-primary">
                    /blog/{generateSlug(formData.title)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;